import React from "react";
import { Link } from "react-router-dom";
import { useProducts } from "../context/ProductContext";

const CategoriesPage = () => {
  const { products, categories } = useProducts();

  const getCategoryProductCount = (categoryId) => {
    return products.filter((p) => p.category === categoryId).length;
  };

  // Map category IDs to image filenames
  const getCategoryImage = (categoryId) => {
    const imageMap = {
      "ghee-and-oil": "GheeAndOil.png",
      beverages: "Beverages.png",
      "malt-and-health-mix": "MaltAndHealthMix.png",
      "palm-products": "Palm.png",
      "rice-millets": "RiceAndMillets.png",
      snacks: "Snacks.png",
      tiffin: "Tiffin.png",
    };
    return `/category_images/${imageMap[categoryId]}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4">
            Explore Our Categories
          </h1>
          <p className="text-base md:text-lg text-green-50 max-w-2xl mx-auto">
            Discover premium quality products across our carefully curated
            categories
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {categories.map((category) => {
            const productCount = getCategoryProductCount(category.id);
            return (
              <Link
                key={category.id}
                to="/products"
                state={{ category: category.id }}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Image Container with Gradient Overlay */}
                <div className="relative aspect-square overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <img
                    src={getCategoryImage(category.id)}
                    alt={category.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />

                  {/* Product Count Badge */}
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full z-20">
                    <span className="text-xs font-semibold text-gray-800">
                      {productCount} {productCount === 1 ? "item" : "items"}
                    </span>
                  </div>

                  {/* Category Icon Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-20 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <div className="flex items-center justify-between">
                      <span className="text-3xl md:text-4xl">
                        {category.icon}
                      </span>
                      <div className="bg-green-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category Info */}
                <div className="p-4 bg-white">
                  <h3 className="text-base md:text-lg font-bold text-gray-800 mb-1 group-hover:text-green-600 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 flex items-center gap-1">
                    <span>Explore collection</span>
                    <svg
                      className="w-3 h-3 transform group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white border-t border-gray-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Why Choose Us?
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              Quality products delivered with care
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl md:text-4xl">🎯</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2 text-sm md:text-base">
                Wide Selection
              </h3>
              <p className="text-xs md:text-sm text-gray-600">
                Diverse range of premium products
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl md:text-4xl">🚚</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2 text-sm md:text-base">
                Fast Delivery
              </h3>
              <p className="text-xs md:text-sm text-gray-600">
                Quick & reliable shipping
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl md:text-4xl">✨</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2 text-sm md:text-base">
                Fresh Quality
              </h3>
              <p className="text-xs md:text-sm text-gray-600">
                Always fresh & authentic
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-3xl md:text-4xl">💰</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2 text-sm md:text-base">
                Best Value
              </h3>
              <p className="text-xs md:text-sm text-gray-600">
                Competitive prices daily
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Shopping?
          </h2>
          <p className="text-green-50 mb-6 text-sm md:text-base">
            Browse through our categories and find your favorite products
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-white text-green-600 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-green-50 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 duration-300"
          >
            <span>View All Products</span>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
